import { Box, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { HouseholdLoadingSteps } from '@/modules/household/components/HouseholdLoadingSteps/HouseholdLoadingSteps';
import { romie, ROMIE_BOLD_WEIGHT } from '@/styles/fonts/romie';
import { COLORS } from '@/styles/tokens/colors';
import { TITLE_SERIF_SIZING } from '@/styles/tokens/fonts';

import { HouseholdLoading_StateFragment } from './graphql/KGLoading.generated';
import { useHouseholdLoadingStep } from './hooks/useHouseholdLoadingStep';
import { HouseholdLoadingGraph } from './HouseholdLoadingGraph';
import { PREVIEW_CONFIG } from './householdLoadingGraph.config';
import { householdLoadingPreview } from './householdLoadingPreview';

const CONTENT_MAX_WIDTH = 740;
const {
  ILLUSTRATIVE_NODE_COUNT,
  ILLUSTRATIVE_KIND_COUNT,
  ILLUSTRATIVE_HUB_COUNT,
  MAX_PREVIEW_NODE_CANDIDATES,
} = PREVIEW_CONFIG;

export function HouseholdSetupLoading({
  householdName,
  snapshot,
  validationPrompt,
  preparingQuestions,
  initialSetup = false,
  firstDocumentUpload = initialSetup,
}: {
  householdName: string;
  snapshot: HouseholdLoading_StateFragment;
  validationPrompt?: ReactNode;
  preparingQuestions?: boolean;
  initialSetup?: boolean;
  firstDocumentUpload?: boolean;
}) {
  const setupAction = firstDocumentUpload ? 'Setting up' : 'Updating';
  const step = useHouseholdLoadingStep({
    snapshot,
    preparingQuestions,
  });
  // Preserve the last factual preview through the question-readiness handoff.
  const preview = householdLoadingPreview(snapshot, {
    keepCompletedPreview: true,
  });
  // Empty previews use an unlabelled decorative graph, never invented records.
  const illustrative = preview.nodes.length === 0;
  const nodes = illustrative
    ? Array.from({ length: ILLUSTRATIVE_NODE_COUNT }, (_, index) => ({
        id: `illustration-${index}`,
        name: '',
        kind: `${index % ILLUSTRATIVE_KIND_COUNT}`,
      }))
    : preview.nodes.slice(0, MAX_PREVIEW_NODE_CANDIDATES);
  const edges = illustrative
    ? nodes.slice(1).map((node, index) => ({
        id: node.id,
        // Connect six illustrative hubs, then attach their satellite nodes.
        sourceID: nodes[index % ILLUSTRATIVE_HUB_COUNT]!.id,
        targetID: node.id,
        kind: '',
      }))
    : preview.relationships;
  return (
    <Stack
      component="section"
      sx={{
        bgcolor: COLORS.PRIMITIVES.WHITE,
        color: COLORS.TEXT.PRIMARY,
        alignItems: 'flex-start',
        p: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          width: '100%',
          maxWidth: CONTENT_MAX_WIDTH,
          gridTemplateRows: validationPrompt ? '1fr' : '0fr',
          opacity: validationPrompt ? 1 : 0,
          transition: 'grid-template-rows 650ms ease, opacity 650ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Box sx={{ overflow: 'hidden', minHeight: 0 }}>{validationPrompt}</Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 3,
        }}
      >
        {!validationPrompt && (
          <Stack
            spacing={1.5}
            sx={{
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant="h1_display"
              component="h2"
              sx={{
                fontFamily: romie.style.fontFamily,
                fontWeight: ROMIE_BOLD_WEIGHT,
                ...TITLE_SERIF_SIZING[initialSetup ? 'xl' : 'l'],
                maxWidth: CONTENT_MAX_WIDTH,
              }}
            >
              {preparingQuestions
                ? 'Preparing a few facts to confirm'
                : `${setupAction} the ${householdName} household`}
            </Typography>
            <HouseholdLoadingSteps step={step} />
          </Stack>
        )}
        <Box sx={{ width: '100%', alignSelf: 'center' }}>
          <HouseholdLoadingGraph
            nodes={nodes}
            edges={edges}
            illustrative={illustrative}
          />
        </Box>
      </Box>
    </Stack>
  );
}
